import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ChevronLeftIcon, ChevronRightIcon, X } from 'lucide-react';
const achievements = [{
  image: 'https://cdn.magicpatterns.com/uploads/esHfUc1dayE1dbJP8QXMN9/1.jpg',
  title: 'Graduation Ceremony 2024',
  description: 'Outstanding performance in O Level & A Level Cambridge 2023 Exams'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/6MUDFtLWPf41CLB2HvUi8m/2.jpg',
  title: 'Graduation Ceremony 2023',
  description: 'Best achievers at MDIC in O Level & A Level CAIE 2022 Exams'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/jNKfEHr7ArZ4yZR7UfEd7j/3.jpg',
  title: 'National Competition Winners',
  description: 'MDIC students recognized for excellence in national competitions'
}];
export function Achievements() {
  const {
    ref,
    isVisible
  } = useScrollReveal();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [direction, setDirection] = useState(0);
  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentIndex(prev => (prev + 1) % achievements.length);
  }, []);
  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentIndex(prev => (prev - 1 + achievements.length) % achievements.length);
  }, []);
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [isVisible, nextSlide]);
  const getVisibleIndices = () => {
    const indices = [];
    for (let i = 0; i < 3; i++) {
      indices.push((currentIndex + i) % achievements.length);
    }
    return indices;
  };
  const visibleIndices = getVisibleIndices();
  return <section className="relative py-16" style={{
    backgroundColor: '#b2d1cf'
  }} data-id="element-20">
      <div className="absolute inset-0" data-id="element-21" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22">
        <div className="text-center mb-14" data-id="element-23">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4" data-id="element-24">
            Our Achievements
          </motion.h2>
          <motion.div initial={{
          scaleX: 0
        }} animate={isVisible ? {
          scaleX: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="h-1 w-24 bg-gold mx-auto mb-4 origin-center" data-id="element-25" />
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-lg text-text-muted max-w-2xl mx-auto" data-id="element-26">
            A legacy of excellence and continuous growth
          </motion.p>
        </div>

        {/* Slider */}
        <div ref={ref} className="relative" data-id="element-27">
          {/* Navigation Arrows */}
          <button onClick={prevSlide} className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 bg-primary/20 hover:bg-primary/40 backdrop-blur-sm text-primary rounded-full p-2 md:p-3 transition-all shadow-lg hover:shadow-xl hover:scale-110" data-id="element-28">
            <ChevronLeftIcon size={24} data-id="element-29" />
          </button>
          <button onClick={nextSlide} className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 bg-primary/20 hover:bg-primary/40 backdrop-blur-sm text-primary rounded-full p-2 md:p-3 transition-all shadow-lg hover:shadow-xl hover:scale-110" data-id="element-30">
            <ChevronRightIcon size={24} data-id="element-31" />
          </button>

          {/* Desktop: 3 images */}
          <div className="hidden md:grid grid-cols-3 gap-6" data-id="element-32">
            {visibleIndices.map((itemIndex, posIndex) => <motion.div key={`${currentIndex}-${posIndex}`} initial={{
            opacity: 0,
            scale: 0.85,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: posIndex * 0.1,
            ease: 'easeOut'
          }} onClick={() => setSelectedImage(itemIndex)} className="group cursor-pointer" data-id="element-33">
                <div className="relative overflow-hidden rounded-xl shadow-2xl" data-id="element-34">
                  <div className="aspect-[4/3] overflow-hidden" data-id="element-35">
                    <img src={achievements[itemIndex].image} alt={achievements[itemIndex].title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" data-id="element-36" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" data-id="element-37" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300" data-id="element-38">
                    <h3 className="text-lg font-heading font-bold text-white mb-1" data-id="element-39">
                      {achievements[itemIndex].title}
                    </h3>
                    <p className="text-sm text-white/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-40">
                      {achievements[itemIndex].description}
                    </p>
                  </div>
                  <div className="absolute inset-0 border-2 border-white/0 group-hover:border-gold/50 rounded-xl transition-all duration-300" data-id="element-41" />
                </div>
              </motion.div>)}
          </div>

          {/* Mobile: 1 image */}
          <div className="md:hidden" data-id="element-42">
            <AnimatePresence mode="wait" data-id="element-43">
              <motion.div key={currentIndex} initial={{
              opacity: 0,
              x: direction >= 0 ? 100 : -100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction >= 0 ? -100 : 100
            }} transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} onClick={() => setSelectedImage(currentIndex)} className="group cursor-pointer" data-id="element-44">
                <div className="relative overflow-hidden rounded-xl shadow-2xl" data-id="element-45">
                  <div className="aspect-[4/3] overflow-hidden" data-id="element-46">
                    <img src={achievements[currentIndex].image} alt={achievements[currentIndex].title} className="w-full h-full object-cover" data-id="element-47" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60" data-id="element-48" />
                  <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-49">
                    <h3 className="text-lg font-heading font-bold text-white mb-1" data-id="element-50">
                      {achievements[currentIndex].title}
                    </h3>
                    <p className="text-sm text-white/80" data-id="element-51">
                      {achievements[currentIndex].description}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-3 mt-8" data-id="element-52">
            {achievements.map((_, index) => <button key={index} onClick={() => {
            setDirection(index > currentIndex ? 1 : -1);
            setCurrentIndex(index);
          }} className={`transition-all duration-300 rounded-full ${index === currentIndex ? 'w-8 h-3 bg-gold' : 'w-3 h-3 bg-primary/30 hover:bg-primary/50'}`} data-id="element-53" />)}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-54">
        {selectedImage !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSelectedImage(null)} className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" data-id="element-55">
            <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-gold transition-colors z-10" data-id="element-56">
              <X size={32} data-id="element-57" />
            </button>
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.8,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="max-w-5xl w-full" onClick={e => e.stopPropagation()} data-id="element-58">
              <img src={achievements[selectedImage].image} alt={achievements[selectedImage].title} className="w-full h-auto max-h-[85vh] object-contain rounded-lg" data-id="element-59" />
              <div className="text-center mt-4" data-id="element-60">
                <h3 className="text-xl font-heading font-bold text-white" data-id="element-61">
                  {achievements[selectedImage].title}
                </h3>
                <p className="text-white/70 mt-1" data-id="element-62">
                  {achievements[selectedImage].description}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}